import {
  Eye,
  EyeSlash,
  PencilSimple,
  UserRectangle,
} from '@phosphor-icons/react';
import { PermissionCategoryModel } from 'api-hooks/role';
import { OnChangeParams, Option, Select } from 'baseui/select';
import { toSentenceCase } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import CheckBoxInput from 'components/elements/inputs/check-box-input';
import Text from 'components/elements/text';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { RoleFormType } from './form-type';

function getRoleItemIcon(size: number, role: string) {
  switch (role) {
    case 'restricted-viewer':
      return <EyeSlash size={size} />;
    case 'manager':
    case 'core':
      return <UserRectangle size={size} />;
    case 'editor':
      return <PencilSimple size={size} />;
    case 'viewer':
    case 'unrestricted-viewer':
    default:
      return <Eye size={size} />;
  }
}

function getRoleDescription(id: string, t: Translate) {
  const role = id.slice(id.indexOf(':') + 1);
  const section = id.slice(id.lastIndexOf('.') + 1, id.indexOf(':'));
  switch (role) {
    case 'restricted-viewer':
    case 'unrestricted-viewer':
    case 'manager':
      return t(`role:${role}-desc`, {
        extra: t(`role:${section}${role === 'manager' ? '-desc' : ''}`),
      });
    case 'core':
      return t(`role:core-desc`, {
        extra: t(`common:${section}`),
      });
    default:
      return t(`role:${role}-desc`);
  }
}

interface PermissionGroupProps {
  data: PermissionCategoryModel;
}

const getLabel =
  (t: Translate) =>
  ({ option }: { option: Option }) => {
    const id = option.id as string;
    const role = id.slice(id.indexOf(':') + 1);
    const description = getRoleDescription(id, t);
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'row',
          gap: 8,
        }}
      >
        {getRoleItemIcon(24, role)}
        <div>
          <Text variant="LabelSmall">{t(`role:${role}`)}</Text>
          <Text variant="ParagraphXSmall">
            {toSentenceCase(description.toLowerCase())}
          </Text>
        </div>
      </div>
    );
  };

export default function PermissionGroupSelect(props: PermissionGroupProps) {
  const { data } = props;
  const { permissionGroups, category } = data;
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<RoleFormType>();
  const permissions = useWatch({
    name: 'permissions',
    control,
  });

  const options: Option[] = permissionGroups.map((group) => {
    return {
      id: group.id,
    };
  });

  const value = options.find((option) => {
    const id = option.id as string;
    return permissions.includes(id);
  });

  const onKickPermission = React.useCallback(() => {
    const _permissions = permissions.filter((permission) => {
      return !options.find((option) => option.id === permission);
    });
    setValue('permissions', _permissions);

    return _permissions;
  }, [options, permissions, setValue]);

  const onChangeCheckbox = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.currentTarget.checked;
      const id = options?.[0]?.id as string;
      if (checked) {
        setValue('permissions', [...permissions, id]);
      } else {
        onKickPermission();
      }
    },
    [onKickPermission, options, permissions, setValue],
  );

  const onChangeSelect = React.useCallback(
    (params: OnChangeParams) => {
      const id = params.option?.id as string;
      const _permissions = onKickPermission();
      setValue('permissions', [..._permissions, id]);
    },
    [onKickPermission, setValue],
  );

  const { editable } = useFormState();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <CheckBoxInput
        disabled={!editable}
        checked={!!value}
        onChange={onChangeCheckbox}
      >
        {category}
      </CheckBoxInput>
      {!!value && (
        <Select
          disabled={!editable}
          searchable={false}
          options={options}
          clearable={false}
          value={value ? [value] : []}
          labelKey="id"
          valueKey="id"
          getOptionLabel={getLabel(t)}
          getValueLabel={getLabel(t)}
          onChange={onChangeSelect}
        />
      )}
    </div>
  );
}
